import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  where,
} from "firebase/firestore";
import { getOrderCompletionFields } from "./orderCompletion";

export const LOT_TRACKING_VERSION = 1;
export const LEGACY_ORDER_RETURN_LOT_NO = "LEGACY-ORDER-RETURN";
export const LEGACY_LOT_MESSAGE =
  "This record has no lot allocation history. Reconcile it before changing or returning accessory stock.";

export const accessoryStatusForStock = (currentStatus, stockQuantity) => {
  if (currentStatus === "Discontinued") return "Discontinued";
  return (Number(stockQuantity) || 0) <= 0 ? "Out of Stock" : "Active";
};

export const normalizeLotKey = (lotNo) =>
  String(lotNo || "").trim().replace(/\s+/g, " ").toLowerCase();

export const lotIdForNumber = (lotNo) =>
  encodeURIComponent(normalizeLotKey(lotNo));

export const receivingMovementType = (lotExists) =>
  lotExists ? "return" : "add";

const asMillis = (value) => {
  if (!value) return Number.MAX_SAFE_INTEGER;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  const millis = new Date(value).getTime();
  return Number.isNaN(millis) ? Number.MAX_SAFE_INTEGER : millis;
};

export const sortLotsFifo = (lots) =>
  [...lots].sort((a, b) => {
    const incoming = asMillis(a.incomingDate) - asMillis(b.incomingDate);
    if (incoming !== 0) return incoming;
    const created = asMillis(a.createdAt) - asMillis(b.createdAt);
    if (created !== 0) return created;
    return String(a.id).localeCompare(String(b.id));
  });

export const sortAvailableLotsNewestFirst = (lots) =>
  sortLotsFifo(lots)
    .filter((lot) => Number(lot.stockQuantity) > 0)
    .reverse();

const positiveInteger = (value, label = "Quantity") => {
  const number = Number(value);
  if (!Number.isInteger(number) || number <= 0) {
    throw new Error(`${label} must be a positive whole number.`);
  }
  return number;
};

const nonNegativeInteger = (value, label) => {
  const number = Number(value);
  if (!Number.isInteger(number) || number < 0) {
    throw new Error(`${label} must be a non-negative whole number.`);
  }
  return number;
};

const validDate = (value, label, required = true) => {
  if (!value) {
    if (required) throw new Error(`${label} is required.`);
    return null;
  }
  const date = typeof value.toDate === "function"
    ? value.toDate()
    : value instanceof Date
    ? new Date(value.getTime())
    : new Date(value);
  if (Number.isNaN(date.getTime())) throw new Error(`${label} must be a valid date.`);
  return date;
};

const normalizeLotInput = (lot, label) => {
  const lotNo = String((lot && lot.lotNo) || "").trim();
  if (!normalizeLotKey(lotNo)) throw new Error(`${label} Lot No. is required.`);
  return {
    lotNo,
    incomingDate: validDate(lot.incomingDate, `${label} incoming date`),
    expiryDate: validDate(lot.expiryDate, `${label} expiry date`, false),
  };
};

export const validateAccessoryStockReceiptRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error("Select at least one accessory.");
  }
  const accessoryIds = new Set();
  return rows.map((row) => {
    if (!row.accessoryId || accessoryIds.has(row.accessoryId)) {
      throw new Error("Each selected accessory must appear exactly once.");
    }
    accessoryIds.add(row.accessoryId);
    const expectedStock = nonNegativeInteger(row.expectedStock, "Current stock");
    const openingLot = row.openingLot
      ? normalizeLotInput(row.openingLot, "Opening lot")
      : null;
    if (openingLot && expectedStock <= 0) {
      throw new Error("An opening lot is only used for an existing positive balance.");
    }

    let receipt = null;
    if (row.receipt) {
      const quantity = positiveInteger(row.receipt.quantity, "Receipt quantity");
      const mode = row.receipt.mode;
      if (!["opening", "existing", "new"].includes(mode)) {
        throw new Error("Select a valid lot choice for the receipt.");
      }
      if (mode === "opening" && !openingLot) {
        throw new Error("Same as opening lot requires opening-lot details.");
      }
      if (mode === "existing" && !row.receipt.lotId) {
        throw new Error("Select an existing lot.");
      }
      const lot = mode === "new"
        ? normalizeLotInput(row.receipt, "Receipt lot")
        : null;
      receipt = {
        quantity,
        mode,
        lotId: mode === "existing" ? row.receipt.lotId : null,
        lotNo: lot ? lot.lotNo : String(row.receipt.lotNo || "").trim(),
        incomingDate: lot ? lot.incomingDate : null,
        expiryDate: lot ? lot.expiryDate : validDate(
          row.receipt.expiryDate,
          "Receipt expiry date",
          false
        ),
        notes: row.receipt.notes || "",
      };
    }
    if (!openingLot && !receipt) {
      throw new Error("Each selected accessory requires a receipt quantity.");
    }
    return {
      accessoryId: row.accessoryId,
      expectedStock,
      openingLot,
      receipt,
    };
  });
};

export const allocateFifo = (lots, requestedQuantity) => {
  let remaining = positiveInteger(requestedQuantity);
  const updatedLots = sortLotsFifo(lots).map((lot) => ({ ...lot }));
  const allocations = [];

  updatedLots.forEach((lot) => {
    if (remaining <= 0) return;
    const available = Number(lot.stockQuantity) || 0;
    if (available <= 0) return;
    const quantity = Math.min(available, remaining);
    lot.stockQuantity = available - quantity;
    remaining -= quantity;
    allocations.push({
      lotId: lot.id,
      lotNo: lot.lotNo,
      quantity,
      incomingDate: lot.incomingDate,
    });
  });

  if (remaining > 0) {
    const available = requestedQuantity - remaining;
    throw new Error(
      `Insufficient lot stock. Available: ${available}, Requested: ${requestedQuantity}`
    );
  }

  return { lots: updatedLots, allocations };
};

export const allocateSelectedLot = (lots, lotId, requestedQuantity) => {
  const quantity = positiveInteger(requestedQuantity);
  if (!lotId) throw new Error("Select a lot for this stock adjustment.");

  const updatedLots = (lots || []).map((lot) => ({ ...lot }));
  const selectedLot = updatedLots.find((lot) => lot.id === lotId);
  if (!selectedLot) {
    throw new Error("The selected lot no longer exists. Reload and try again.");
  }

  const lotLabel = selectedLot.lotNo || lotId;
  const available = nonNegativeInteger(
    selectedLot.stockQuantity || 0,
    `${lotLabel} stock`
  );
  if (available < quantity) {
    throw new Error(
      `Insufficient stock in selected lot ${lotLabel}. Available: ${available}, Requested: ${quantity}`
    );
  }

  selectedLot.stockQuantity = available - quantity;
  return {
    lots: updatedLots,
    allocations: [{
      lotId: selectedLot.id,
      lotNo: selectedLot.lotNo,
      quantity,
      incomingDate: selectedLot.incomingDate,
    }],
  };
};

export const restoreLatestAllocations = (lots, allocations, requestedQuantity) => {
  let remaining = positiveInteger(requestedQuantity);
  const updatedLots = lots.map((lot) => ({ ...lot }));
  const lotMap = new Map(updatedLots.map((lot) => [lot.id, lot]));
  const remainingAllocations = (allocations || []).map((allocation) => ({
    ...allocation,
  }));
  const restored = [];

  for (let index = remainingAllocations.length - 1; index >= 0 && remaining > 0; index -= 1) {
    const allocation = remainingAllocations[index];
    const lot = lotMap.get(allocation.lotId);
    if (!lot) throw new Error(`Allocated lot ${allocation.lotId} no longer exists.`);
    const quantity = Math.min(Number(allocation.quantity) || 0, remaining);
    if (quantity <= 0) continue;
    lot.stockQuantity = (Number(lot.stockQuantity) || 0) + quantity;
    allocation.quantity -= quantity;
    remaining -= quantity;
    restored.unshift({
      lotId: allocation.lotId,
      lotNo: allocation.lotNo || lot.lotNo,
      quantity,
      incomingDate: allocation.incomingDate || lot.incomingDate,
    });
  }

  if (remaining > 0) {
    throw new Error("Cannot restore more stock than the saved lot allocations.");
  }

  return {
    lots: updatedLots,
    allocations: remainingAllocations.filter((allocation) => allocation.quantity > 0),
    restored,
  };
};

export const restoreLegacyOrderQuantity = (
  lots,
  requestedQuantity,
  now = new Date()
) => {
  const quantity = positiveInteger(requestedQuantity);
  const returnLotId = lotIdForNumber(LEGACY_ORDER_RETURN_LOT_NO);
  const updatedLots = (lots || []).map((lot) => ({ ...lot }));
  let returnLot = updatedLots.find((lot) => lot.id === returnLotId);

  if (!returnLot) {
    returnLot = {
      id: returnLotId,
      lotNo: LEGACY_ORDER_RETURN_LOT_NO,
      normalizedLotKey: normalizeLotKey(LEGACY_ORDER_RETURN_LOT_NO),
      stockQuantity: 0,
      receivedQuantity: 0,
      incomingDate: now,
      expiryDate: null,
      createdAt: now,
      initializationSource: "legacy_sale_order_return",
      isNew: true,
    };
    updatedLots.push(returnLot);
  }

  returnLot.stockQuantity = (Number(returnLot.stockQuantity) || 0) + quantity;
  if (returnLot.isNew) {
    returnLot.receivedQuantity =
      (Number(returnLot.receivedQuantity) || 0) + quantity;
  }

  return {
    lots: updatedLots,
    restored: [{
      lotId: returnLot.id,
      lotNo: returnLot.lotNo,
      quantity,
      incomingDate: returnLot.incomingDate,
    }],
  };
};

const lotTotal = (lots) =>
  lots.reduce((sum, lot) => sum + (Number(lot.stockQuantity) || 0), 0);

const allocationTotal = (allocations, label = "Lot allocation") =>
  (allocations || []).reduce(
    (sum, allocation) =>
      sum + positiveInteger(allocation.quantity, `${label} quantity`),
    0
  );

export const getOrderItemInventoryBreakdown = (item) => {
  const itemName = item.productName || item.productId || "Accessory";
  const quantity = positiveInteger(item.quantity, `${itemName} quantity`);
  const lotAllocations = Array.isArray(item.lotAllocations)
    ? item.lotAllocations.map((allocation) => ({ ...allocation }))
    : [];
  const allocatedQuantity = allocationTotal(
    lotAllocations,
    `${itemName} lot allocation`
  );

  if (allocatedQuantity > quantity) {
    throw new Error(
      `${itemName} has more allocated stock than its order quantity.`
    );
  }

  return {
    quantity,
    lotAllocations,
    allocatedQuantity,
    legacyQuantity: quantity - allocatedQuantity,
  };
};

export const buildParentLotSnapshot = (lots) => {
  const oldest = sortLotsFifo(lots).find((lot) => Number(lot.stockQuantity) > 0);
  return oldest
    ? {
        stockQuantity: lotTotal(lots),
        lotNo: oldest.lotNo,
        incomingDate: oldest.incomingDate,
        expiryDate: oldest.expiryDate || null,
      }
    : {
        stockQuantity: 0,
        lotNo: null,
        incomingDate: null,
        expiryDate: null,
      };
};

const assertReconciled = (parent, lots) => {
  if (parent.lotTrackingVersion !== LOT_TRACKING_VERSION) {
    throw new Error(LEGACY_LOT_MESSAGE);
  }
  const parentTotal = Number(parent.stockQuantity) || 0;
  const childTotal = lotTotal(lots);
  if (parentTotal !== childTotal) {
    throw new Error(
      `Lot balance mismatch. Parent: ${parentTotal}, lots: ${childTotal}. Reconcile before continuing.`
    );
  }
};

export const planAccessoryStockReceipt = (parent, lots, row, now = new Date()) => {
  const itemName = parent.itemName || parent.itemCode || row.accessoryId;
  const currentStock = nonNegativeInteger(parent.stockQuantity || 0, `${itemName} stock`);
  if (currentStock !== row.expectedStock) {
    throw new Error(
      `${itemName} stock changed from ${row.expectedStock} to ${currentStock}. Reload and try again.`
    );
  }

  const tracked = parent.lotTrackingVersion === LOT_TRACKING_VERSION;
  const workingLots = (lots || []).map((lot) => ({ ...lot, isNew: false }));
  if (tracked) {
    if (row.openingLot) throw new Error(`${itemName} is already lot tracked. Reload and try again.`);
    if (!row.receipt) throw new Error(`${itemName} requires a positive receipt quantity.`);
    assertReconciled(parent, workingLots);
  } else if (workingLots.length > 0) {
    throw new Error(
      `${itemName} already has lot documents but is not marked as tracked. Reconcile it separately.`
    );
  }

  let openingLot = null;
  if (!tracked && currentStock > 0) {
    if (!row.openingLot) {
      throw new Error(`${itemName} requires opening Lot No. and incoming date.`);
    }
    const openingLotId = lotIdForNumber(row.openingLot.lotNo);
    openingLot = {
      id: openingLotId,
      lotNo: row.openingLot.lotNo,
      normalizedLotKey: normalizeLotKey(row.openingLot.lotNo),
      stockQuantity: currentStock,
      receivedQuantity: currentStock,
      incomingDate: row.openingLot.incomingDate,
      expiryDate: row.openingLot.expiryDate || null,
      createdAt: parent.createdAt || now,
      initializationSource: "add_stock_modal_legacy_opening",
      isNew: true,
    };
    workingLots.push(openingLot);
  } else if (!tracked && row.openingLot) {
    throw new Error(`${itemName} has no existing balance and does not need an opening lot.`);
  }

  if (!tracked && currentStock === 0 && !row.receipt) {
    throw new Error(`${itemName} requires a positive first receipt quantity.`);
  }

  let movement = null;
  if (row.receipt) {
    const receipt = row.receipt;
    let targetLot = null;
    if (receipt.mode === "opening") {
      if (!openingLot) throw new Error(`${itemName} has no opening lot for this receipt.`);
      targetLot = openingLot;
    } else if (receipt.mode === "existing") {
      targetLot = workingLots.find((lot) => lot.id === receipt.lotId);
      if (!targetLot) throw new Error(`The selected lot for ${itemName} no longer exists.`);
    } else {
      const targetLotId = lotIdForNumber(receipt.lotNo);
      targetLot = workingLots.find((lot) => lot.id === targetLotId);
      if (openingLot && targetLot && targetLot.id === openingLot.id) {
        throw new Error(
          `${itemName}: the receipt Lot No. matches the opening lot. Choose Same as opening lot.`
        );
      }
      if (!targetLot) {
        targetLot = {
          id: targetLotId,
          lotNo: receipt.lotNo,
          normalizedLotKey: normalizeLotKey(receipt.lotNo),
          stockQuantity: 0,
          receivedQuantity: receipt.quantity,
          incomingDate: receipt.incomingDate,
          expiryDate: receipt.expiryDate || null,
          createdAt: now,
          isNew: true,
        };
        workingLots.push(targetLot);
      }
    }

    const targetKey = targetLot.normalizedLotKey || normalizeLotKey(targetLot.lotNo);
    if (receipt.mode === "existing" && receipt.lotNo) {
      const receiptKey = normalizeLotKey(receipt.lotNo);
      if (receiptKey && receiptKey !== targetKey) {
        throw new Error(`The selected lot for ${itemName} does not match its Lot No.`);
      }
    }
    const existedBeforeReceipt = !targetLot.isNew || targetLot === openingLot;
    targetLot.stockQuantity = (Number(targetLot.stockQuantity) || 0) + receipt.quantity;
    if (receipt.expiryDate) targetLot.expiryDate = receipt.expiryDate;
    movement = {
      quantity: receipt.quantity,
      type: receivingMovementType(existedBeforeReceipt),
      notes: receipt.notes,
      allocation: {
        lotId: targetLot.id,
        lotNo: targetLot.lotNo,
        quantity: receipt.quantity,
        incomingDate: targetLot.incomingDate,
      },
    };
  }

  return {
    initialized: !tracked,
    lots: workingLots,
    movement,
    parentSnapshot: buildParentLotSnapshot(workingLots),
  };
};

const actorName = (actor) => actor.displayName || actor.email || "Unknown";

const stockLogData = ({ accessoryId, accessory, quantity, before, type, notes, actor, allocations }) => ({
  itemId: accessoryId,
  itemName: accessory.itemName,
  itemType: "Accessory",
  quantity,
  stockBefore: before,
  stockAfter: before + quantity,
  type,
  notes: notes || "",
  lotAllocations: allocations.filter((allocation) => allocation.quantity > 0),
  createdBy: actorName(actor),
  createdAt: new Date(),
  date: new Date(),
});

const loadLotRefs = async (database, accessoryId) => {
  const snapshot = await getDocs(collection(database, "accessories", accessoryId, "lots"));
  return snapshot.docs.map((lotDoc) => lotDoc.ref);
};

const readInventory = async (
  transaction,
  database,
  accessoryId,
  lotRefs,
  { allowLegacy = false } = {}
) => {
  const parentRef = doc(database, "accessories", accessoryId);
  const parentSnap = await transaction.get(parentRef);
  if (!parentSnap.exists()) throw new Error("Accessory item not found.");
  const lotSnaps = [];
  for (const lotRef of lotRefs) lotSnaps.push(await transaction.get(lotRef));
  const lots = lotSnaps
    .filter((snapshot) => snapshot.exists())
    .map((snapshot) => ({ id: snapshot.id, ref: snapshot.ref, ...snapshot.data() }));
  const parent = parentSnap.data();
  const tracked = parent.lotTrackingVersion === LOT_TRACKING_VERSION;
  if (tracked) {
    assertReconciled(parent, lots);
  } else if (!allowLegacy) {
    throw new Error(LEGACY_LOT_MESSAGE);
  } else if (lots.length > 0) {
    throw new Error(
      "Accessory lot documents exist without completed lot migration. Reconcile this accessory before continuing."
    );
  }
  return {
    parentRef,
    parent,
    lots,
    tracked,
    legacyStockQuantity: tracked
      ? 0
      : nonNegativeInteger(
          parent.stockQuantity || 0,
          `${parent.itemName || parent.itemCode || "Accessory"} stock`
        ),
  };
};

const writeInventory = (transaction, inventory, lots, actor) => {
  const parentSnapshot = buildParentLotSnapshot(lots);
  lots.forEach((lot) => {
    if (lot.isNew) {
      transaction.set(lot.ref, {
        lotNo: lot.lotNo,
        normalizedLotKey: lot.normalizedLotKey || normalizeLotKey(lot.lotNo),
        stockQuantity: lot.stockQuantity,
        receivedQuantity: lot.receivedQuantity,
        incomingDate: lot.incomingDate,
        expiryDate: lot.expiryDate || null,
        createdAt: lot.createdAt || new Date(),
        createdBy: actorName(actor),
        updatedAt: new Date(),
        updatedBy: actorName(actor),
        initializationSource: lot.initializationSource || "inventory_write",
      });
    } else {
      transaction.update(lot.ref, {
        stockQuantity: lot.stockQuantity,
        updatedAt: new Date(),
        updatedBy: actorName(actor),
      });
    }
  });
  transaction.update(inventory.parentRef, {
    ...parentSnapshot,
    status: accessoryStatusForStock(
      inventory.parent.status,
      parentSnapshot.stockQuantity
    ),
    lotTrackingVersion: LOT_TRACKING_VERSION,
    updatedAt: new Date(),
    updatedBy: actorName(actor),
  });
};

const inventoryTotal = (inventory) =>
  inventory.tracked
    ? lotTotal(inventory.lots)
    : inventory.legacyStockQuantity;

export const consumeOrderInventory = (inventory, requestedQuantity) => {
  const quantity = positiveInteger(requestedQuantity);
  const before = inventoryTotal(inventory);

  if (inventory.tracked) {
    const result = allocateFifo(inventory.lots, quantity);
    inventory.lots = result.lots;
    return { before, allocations: result.allocations };
  }

  if (inventory.legacyStockQuantity < quantity) {
    throw new Error(
      `Insufficient stock. Available: ${inventory.legacyStockQuantity}, Requested: ${quantity}`
    );
  }
  inventory.legacyStockQuantity -= quantity;
  return { before, allocations: [] };
};

const restoreLegacyInventory = (
  inventory,
  requestedQuantity,
  legacyReturnRef
) => {
  const quantity = positiveInteger(requestedQuantity);

  if (!inventory.tracked) {
    inventory.legacyStockQuantity += quantity;
    return [];
  }

  const result = restoreLegacyOrderQuantity(inventory.lots, quantity);
  if (result.lots.some((lot) => lot.isNew) && !legacyReturnRef) {
    throw new Error("Legacy return lot reference is missing.");
  }
  inventory.lots = result.lots.map((lot) =>
    lot.isNew ? { ...lot, ref: legacyReturnRef } : lot
  );
  return result.restored;
};

const writeInventoryState = (transaction, inventory, actor) => {
  if (inventory.tracked) {
    writeInventory(transaction, inventory, inventory.lots, actor);
    return;
  }

  transaction.update(inventory.parentRef, {
    stockQuantity: inventory.legacyStockQuantity,
    status: accessoryStatusForStock(
      inventory.parent.status,
      inventory.legacyStockQuantity
    ),
    updatedAt: new Date(),
    updatedBy: actorName(actor),
  });
};

export const restoreOrderItemQuantity = (
  inventory,
  item,
  requestedQuantity,
  legacyReturnRef
) => {
  const breakdown = getOrderItemInventoryBreakdown(item);
  const quantity = positiveInteger(
    requestedQuantity,
    `${item.productName || "Accessory"} return quantity`
  );
  if (quantity > breakdown.quantity) {
    throw new Error(
      `Cannot return more ${item.productName || "accessory"} stock than the order contains.`
    );
  }

  const allocatedToRestore = Math.min(
    breakdown.allocatedQuantity,
    quantity
  );
  let remainingAllocations = breakdown.lotAllocations;
  let restoredAllocations = [];

  if (allocatedToRestore > 0) {
    if (!inventory.tracked) {
      throw new Error(
        `${item.productName || "Accessory"} has saved allocations but its inventory is not lot-tracked.`
      );
    }
    const result = restoreLatestAllocations(
      inventory.lots,
      breakdown.lotAllocations,
      allocatedToRestore
    );
    inventory.lots = result.lots;
    remainingAllocations = result.allocations;
    restoredAllocations = result.restored;
  }

  const legacyToRestore = quantity - allocatedToRestore;
  if (legacyToRestore > 0) {
    restoredAllocations = [
      ...restoredAllocations,
      ...restoreLegacyInventory(
        inventory,
        legacyToRestore,
        legacyReturnRef
      ),
    ];
  }

  return {
    remainingAllocations,
    restoredAllocations,
    allocatedQuantity: allocatedToRestore,
    legacyQuantity: legacyToRestore,
  };
};

export const validateOpeningStock = (accessoryData) => {
  const stockQuantity = Number(accessoryData.stockQuantity) || 0;
  if (!Number.isInteger(stockQuantity) || stockQuantity < 0) {
    throw new Error("Stock quantity must be a non-negative whole number.");
  }
  if (stockQuantity > 0 && (!normalizeLotKey(accessoryData.lotNo) || !accessoryData.incomingDate)) {
    throw new Error("Lot No. and incoming date are required for positive opening stock.");
  }
  return stockQuantity;
};

export const createAccessoryWithOpeningLot = async (database, accessoryData, actor) => {
  const stockQuantity = validateOpeningStock(accessoryData);

  const parentRef = doc(collection(database, "accessories"));
  await runTransaction(database, async (transaction) => {
    const now = new Date();
    const parent = {
      ...accessoryData,
      stockQuantity,
      status: accessoryStatusForStock(accessoryData.status, stockQuantity),
      lotNo: stockQuantity > 0 ? String(accessoryData.lotNo).trim() : null,
      incomingDate: stockQuantity > 0 ? accessoryData.incomingDate : null,
      expiryDate: stockQuantity > 0 ? (accessoryData.expiryDate || null) : null,
      lotTrackingVersion: LOT_TRACKING_VERSION,
      createdAt: now,
      createdBy: actorName(actor),
      updatedAt: now,
      updatedBy: actorName(actor),
    };
    transaction.set(parentRef, parent);
    if (stockQuantity > 0) {
      const lotId = lotIdForNumber(accessoryData.lotNo);
      const lotRef = doc(database, "accessories", parentRef.id, "lots", lotId);
      const allocation = [{
        lotId,
        lotNo: String(accessoryData.lotNo).trim(),
        quantity: stockQuantity,
        incomingDate: accessoryData.incomingDate,
      }];
      transaction.set(lotRef, {
        lotNo: String(accessoryData.lotNo).trim(),
        normalizedLotKey: normalizeLotKey(accessoryData.lotNo),
        stockQuantity,
        receivedQuantity: stockQuantity,
        incomingDate: accessoryData.incomingDate,
        expiryDate: accessoryData.expiryDate || null,
        createdAt: now,
        createdBy: actorName(actor),
        updatedAt: now,
        updatedBy: actorName(actor),
      });
      transaction.set(doc(collection(database, "stockLogs")), stockLogData({
        accessoryId: parentRef.id,
        accessory: accessoryData,
        quantity: stockQuantity,
        before: 0,
        type: "add",
        notes: "Opening accessory stock",
        actor,
        allocations: allocation,
      }));
    }
  });
  return parentRef.id;
};

const receiptCandidateLotIds = (row) => {
  const lotIds = [];
  if (row.openingLot) lotIds.push(lotIdForNumber(row.openingLot.lotNo));
  if (row.receipt) {
    if (row.receipt.mode === "existing") lotIds.push(row.receipt.lotId);
    if (row.receipt.mode === "new") lotIds.push(lotIdForNumber(row.receipt.lotNo));
  }
  return lotIds.filter(Boolean);
};

export const receiveAccessoryLots = async (database, rows, actor) => {
  const preparedRows = validateAccessoryStockReceiptRows(rows);
  const refsByAccessory = new Map();
  for (const row of preparedRows) {
    const existingRefs = await loadLotRefs(database, row.accessoryId);
    const refs = new Map(existingRefs.map((ref) => [ref.id, ref]));
    receiptCandidateLotIds(row).forEach((lotId) => {
      if (!refs.has(lotId)) {
        refs.set(
          lotId,
          doc(database, "accessories", row.accessoryId, "lots", lotId)
        );
      }
    });
    refsByAccessory.set(row.accessoryId, refs);
  }

  return runTransaction(database, async (transaction) => {
    const parentRecords = [];
    for (const row of preparedRows) {
      const parentRef = doc(database, "accessories", row.accessoryId);
      const parentSnap = await transaction.get(parentRef);
      parentRecords.push({ row, parentRef, parentSnap });
    }

    const lotSnapshotsByAccessory = new Map();
    for (const row of preparedRows) {
      const snapshots = [];
      for (const lotRef of refsByAccessory.get(row.accessoryId).values()) {
        snapshots.push(await transaction.get(lotRef));
      }
      lotSnapshotsByAccessory.set(row.accessoryId, snapshots);
    }

    const now = new Date();
    const plannedRecords = parentRecords.map(({ row, parentRef, parentSnap }) => {
      if (!parentSnap.exists()) throw new Error("Accessory item not found.");
      const lots = lotSnapshotsByAccessory.get(row.accessoryId)
        .filter((snapshot) => snapshot.exists())
        .map((snapshot) => ({ id: snapshot.id, ...snapshot.data() }));
      return {
        row,
        parentRef,
        parent: parentSnap.data(),
        plan: planAccessoryStockReceipt(parentSnap.data(), lots, row, now),
      };
    });

    plannedRecords.forEach(({ row, parentRef, parent, plan }) => {
      const refs = refsByAccessory.get(row.accessoryId);
      plan.lots.forEach((lot) => {
        const lotRef = refs.get(lot.id) || doc(
          database,
          "accessories",
          row.accessoryId,
          "lots",
          lot.id
        );
        if (lot.isNew) {
          const newLotData = {
            lotNo: lot.lotNo,
            normalizedLotKey: lot.normalizedLotKey,
            stockQuantity: lot.stockQuantity,
            receivedQuantity: lot.receivedQuantity,
            incomingDate: lot.incomingDate,
            expiryDate: lot.expiryDate || null,
            createdAt: lot.createdAt || now,
            createdBy: actorName(actor),
            updatedAt: now,
            updatedBy: actorName(actor),
          };
          if (lot.initializationSource) {
            newLotData.initializationSource = lot.initializationSource;
          }
          transaction.set(lotRef, newLotData);
        } else {
          transaction.update(lotRef, {
            stockQuantity: lot.stockQuantity,
            expiryDate: lot.expiryDate || null,
            updatedAt: now,
            updatedBy: actorName(actor),
          });
        }
      });

      const parentUpdate = {
        ...plan.parentSnapshot,
        status: accessoryStatusForStock(
          parent.status,
          plan.parentSnapshot.stockQuantity
        ),
        lotTrackingVersion: LOT_TRACKING_VERSION,
        updatedAt: now,
        updatedBy: actorName(actor),
      };
      if (plan.initialized) {
        parentUpdate.lotInitializedAt = now;
        parentUpdate.lotInitializedBy = actorName(actor);
        parentUpdate.lotInitializationSource = "add_stock_modal";
      }
      transaction.update(parentRef, parentUpdate);

      if (plan.movement) {
        transaction.set(doc(collection(database, "stockLogs")), stockLogData({
          accessoryId: row.accessoryId,
          accessory: parent,
          quantity: plan.movement.quantity,
          before: row.expectedStock,
          type: plan.movement.type,
          notes: plan.movement.notes,
          actor,
          allocations: [plan.movement.allocation],
        }));
      }
    });

    return {
      initializedCount: plannedRecords.filter((record) => record.plan.initialized).length,
      receiptCount: plannedRecords.filter((record) => Boolean(record.plan.movement)).length,
    };
  });
};

export const consumeAccessoryFifo = async (database, movement, actor) => {
  const quantity = positiveInteger(movement.quantity);
  const lotRefs = await loadLotRefs(database, movement.accessoryId);
  return runTransaction(database, async (transaction) => {
    const inventory = await readInventory(
      transaction,
      database,
      movement.accessoryId,
      lotRefs,
      { allowLegacy: true }
    );
    let result;
    if (inventory.tracked) {
      result = allocateSelectedLot(inventory.lots, movement.lotId, quantity);
      inventory.lots = result.lots;
    } else {
      if (movement.lotId) {
        throw new Error(
          "This legacy accessory has no lots. Reload and adjust its legacy stock."
        );
      }
      result = consumeOrderInventory(inventory, quantity);
    }
    writeInventoryState(transaction, inventory, actor);
    transaction.set(doc(collection(database, "stockLogs")), stockLogData({
      accessoryId: movement.accessoryId,
      accessory: inventory.parent,
      quantity: -quantity,
      before: Number(inventory.parent.stockQuantity) || 0,
      type: movement.type || "withdraw",
      notes: movement.notes,
      actor,
      allocations: result.allocations,
    }));
    return result.allocations;
  });
};

export const approveAccessoryMaterialIssue = async (database, issueId, actor) => {
  const issueRef = doc(database, "materialIssues", issueId);
  const preview = await getDoc(issueRef);
  if (!preview.exists()) throw new Error("Material issue request not found.");
  const lotRefs = await loadLotRefs(database, preview.data().itemId);
  return runTransaction(database, async (transaction) => {
    const currentIssue = await transaction.get(issueRef);
    if (!currentIssue.exists()) throw new Error("Material issue request not found.");
    const issue = currentIssue.data();
    if (issue.status !== "Pending") throw new Error("This request is no longer Pending.");
    const inventory = await readInventory(
      transaction,
      database,
      issue.itemId,
      lotRefs,
      { allowLegacy: true }
    );
    const result = consumeOrderInventory(inventory, issue.quantity);
    writeInventoryState(transaction, inventory, actor);
    transaction.update(issueRef, {
      status: "Approved",
      lotAllocations: result.allocations,
      approvedBy: actorName(actor),
      approvedAt: new Date(),
      updatedAt: new Date(),
    });
    transaction.set(doc(collection(database, "stockLogs")), stockLogData({
      accessoryId: issue.itemId,
      accessory: inventory.parent,
      quantity: -Number(issue.quantity),
      before: result.before,
      type: "material_issue",
      notes: inventory.tracked
        ? `Material Issue Request: ${issue.requestNo} - ${issue.reason}`
        : `Material Issue Request: ${issue.requestNo} - ${issue.reason} - issued from old stock`,
      actor,
      allocations: result.allocations,
    }));
    return result.allocations;
  });
};

export const isAccessoryOrderItem = (item) => {
  const accessoryTypes = ["Mask", "Tube", "Filter", "Hose", "Humidifier Chamber", "SD Card", "Battery", "Other"];
  const deviceTypes = ["CPAP", "Humidifier", "BiPAP", "APAP"];
  return item && (
    item.itemType === "Accessory" ||
    accessoryTypes.includes(item.productType) ||
    (item.productType && !deviceTypes.includes(item.productType))
  );
};

const uniqueAccessoryIds = (items) => Array.from(new Set(
  (items || []).filter(isAccessoryOrderItem).map((item) => item.productId)
));

const validateAccessoryIds = (accessoryIds) => {
  if (accessoryIds.some((accessoryId) => !String(accessoryId || "").trim())) {
    throw new Error("An accessory in this order has no inventory ID.");
  }
  return accessoryIds;
};

const cancellationItemType = (value) => String(value || "").trim().toUpperCase();

// Cancellation must distinguish catalog membership from the device's type.
// In particular, both catalogs use "Other", and older orders omit itemType.
export const resolveSaleOrderCancellationItems = async (database, items) => {
  const productIds = new Set();
  const accessoryItems = [];
  const identityChecks = [];

  for (const item of items || []) {
    if (!item || typeof item !== "object") {
      throw new Error("Order contains an invalid inventory item.");
    }
    const savedProductId = String(item.productId || "").trim();
    const productType = cancellationItemType(item.productType);
    if (cancellationItemType(item.itemType) === "ACCESSORY") {
      accessoryItems.push({ ...item, productId: savedProductId });
      continue;
    }

    const isDevice = ["CPAP", "APAP", "BPAP", "BIPAP", "HUMIDIFIER"].includes(productType);
    const needsCatalogLookup = productType === "OTHER" || !productType;
    if (!isDevice && !needsCatalogLookup) {
      accessoryItems.push({ ...item, productId: savedProductId });
      continue;
    }

    if (savedProductId) {
      if (needsCatalogLookup) {
        const productRef = doc(database, "products", savedProductId);
        const accessoryRef = doc(database, "accessories", savedProductId);
        const [productSnap, accessorySnap] = await Promise.all([
          getDoc(productRef), getDoc(accessoryRef),
        ]);
        if (productSnap.exists() === accessorySnap.exists()) {
          throw new Error(
            `Inventory item ${savedProductId} is missing or ambiguous between products and accessories.`
          );
        }
        identityChecks.push(
          { ref: productRef, exists: productSnap.exists() },
          { ref: accessoryRef, exists: accessorySnap.exists() }
        );
        if (accessorySnap.exists()) {
          accessoryItems.push({ ...item, productId: savedProductId });
          continue;
        }
      }
      productIds.add(savedProductId);
      continue;
    }

    // Older Excel imports saved the serial number but left productId empty.
    const serialNumber = String(item.productSn || "").trim() ||
      String(item.serialNumber || "").trim();
    if (!serialNumber) {
      throw new Error("Product item has no product ID or serial number.");
    }

    const matchingProductIds = Array.from(new Set(
      (await findProductIdsBySerial(database, serialNumber))
        .map((productId) => String(productId || "").trim())
        .filter(Boolean)
    ));

    if (matchingProductIds.length === 0) {
      throw new Error(`Product with serial number ${serialNumber} was not found.`);
    }
    if (matchingProductIds.length > 1) {
      throw new Error(
        `Multiple products use serial number ${serialNumber}. Reconcile the duplicate products before cancelling this order.`
      );
    }

    productIds.add(matchingProductIds[0]);
  }

  return { productIds: Array.from(productIds), accessoryItems, identityChecks };
};

export const isExcelImportedSaleOrder = (order) =>
  ["healthInsurance", "sleepStudyOrder", "refHospitalName"].every(
    (field) => Object.prototype.hasOwnProperty.call(order || {}, field)
  );

const findProductIdsBySerial = async (database, serialNumber) => {
  const snapshot = await getDocs(query(
    collection(database, "products"),
    where("serialNumber", "==", serialNumber)
  ));
  return snapshot.docs.map((productDoc) => productDoc.id);
};

const loadInventoryRefs = async (database, accessoryIds) => {
  const entries = await Promise.all(accessoryIds.map(async (accessoryId) => [
    accessoryId,
    await loadLotRefs(database, accessoryId),
  ]));
  return new Map(entries);
};

const readInventories = async (
  transaction,
  database,
  refsByAccessory,
  options
) => {
  const inventories = new Map();
  for (const [accessoryId, lotRefs] of refsByAccessory.entries()) {
    inventories.set(
      accessoryId,
      await readInventory(transaction, database, accessoryId, lotRefs, options)
    );
  }
  return inventories;
};

const prepareLegacyReturnRefs = (database, refsByAccessory) => {
  const legacyReturnLotId = lotIdForNumber(LEGACY_ORDER_RETURN_LOT_NO);
  return new Map(Array.from(refsByAccessory.entries()).map(
    ([accessoryId, lotRefs]) => {
      const returnRef = doc(
        database,
        "accessories",
        accessoryId,
        "lots",
        legacyReturnLotId
      );
      if (!lotRefs.some((lotRef) => lotRef.id === legacyReturnLotId)) {
        lotRefs.push(returnRef);
      }
      return [accessoryId, returnRef];
    }
  ));
};

export const createSaleOrderWithLots = async (
  database,
  orderData,
  actor,
  providedOrderRef = null
) => {
  const accessoryIds = validateAccessoryIds(uniqueAccessoryIds(orderData.items));
  const refsByAccessory = await loadInventoryRefs(database, accessoryIds);
  const orderRef = providedOrderRef || doc(collection(database, "saleOrders"));

  await runTransaction(database, async (transaction) => {
    const existingOrder = await transaction.get(orderRef);
    if (existingOrder.exists()) {
      throw new Error("Sale order already exists.");
    }
    const inventories = await readInventories(
      transaction,
      database,
      refsByAccessory,
      { allowLegacy: true }
    );
    const nextItems = (orderData.items || []).map((item) => ({ ...item }));
    const logs = [];

    nextItems.forEach((item) => {
      if (!isAccessoryOrderItem(item)) return;
      const inventory = inventories.get(item.productId);
      if (!inventory) {
        throw new Error(`${item.productName || "Accessory"} inventory was not found.`);
      }
      const quantity = positiveInteger(
        item.quantity,
        `${item.productName || "Accessory"} quantity`
      );
      const result = consumeOrderInventory(inventory, quantity);
      item.lotAllocations = result.allocations;
      logs.push(stockLogData({
        accessoryId: item.productId,
        accessory: inventory.parent,
        quantity: -quantity,
        before: result.before,
        type: "sell",
        notes: inventory.tracked
          ? `Sold in order ${orderData.receiptNo} (HN: ${orderData.hn})`
          : `Sold from old stock in order ${orderData.receiptNo} (HN: ${orderData.hn})`,
        actor,
        allocations: result.allocations,
      }));
    });

    inventories.forEach((inventory) =>
      writeInventoryState(transaction, inventory, actor)
    );
    transaction.set(orderRef, {
      ...orderData,
      ...getOrderCompletionFields(null, orderData.status),
      id: orderRef.id,
      items: nextItems,
    });
    logs.forEach((log) => transaction.set(doc(collection(database, "stockLogs")), log));
  });
  return orderRef;
};

const indexItems = (items) => {
  const indexed = new Map();
  (items || []).forEach((item) => {
    if (indexed.has(item.productId)) {
      throw new Error(
        `${item.productName || "An accessory"} appears more than once in the order.`
      );
    }
    indexed.set(item.productId, item);
  });
  return indexed;
};

export const updateSaleOrderWithLots = async (database, orderId, nextOrderData, actor) => {
  const orderRef = doc(database, "saleOrders", orderId);
  const preview = await getDoc(orderRef);
  if (!preview.exists()) throw new Error("Order not found.");
  const manageAccessoryStock = !isExcelImportedSaleOrder(preview.data());
  const accessoryIds = manageAccessoryStock
    ? validateAccessoryIds(Array.from(new Set([
        ...uniqueAccessoryIds(preview.data().items),
        ...uniqueAccessoryIds(nextOrderData.items),
      ])))
    : [];
  const refsByAccessory = await loadInventoryRefs(database, accessoryIds);
  const legacyReturnRefs = prepareLegacyReturnRefs(database, refsByAccessory);

  return runTransaction(database, async (transaction) => {
    const currentSnap = await transaction.get(orderRef);
    if (!currentSnap.exists()) throw new Error("Order not found.");
    const currentOrder = currentSnap.data();
    const completionFields = getOrderCompletionFields(currentOrder, nextOrderData.status);
    const nextItems = (nextOrderData.items || []).map((item) => ({ ...item }));
    const currentManageAccessoryStock = !isExcelImportedSaleOrder(currentOrder);
    if (manageAccessoryStock !== currentManageAccessoryStock) {
      throw new Error("Order data changed. Reload and try again.");
    }
    if (!manageAccessoryStock) {
      transaction.update(orderRef, { ...nextOrderData, ...completionFields, items: nextItems });
      return nextItems;
    }

    const inventories = await readInventories(
      transaction,
      database,
      refsByAccessory,
      { allowLegacy: true }
    );
    const originalById = indexItems(
      (currentOrder.items || []).filter(isAccessoryOrderItem)
    );
    const nextById = indexItems(nextItems.filter(isAccessoryOrderItem));
    const logs = [];

    originalById.forEach((original, accessoryId) => {
      const next = nextById.get(accessoryId);
      const originalBreakdown = getOrderItemInventoryBreakdown(original);
      const originalQuantity = originalBreakdown.quantity;
      const nextQuantity = next
        ? positiveInteger(
            next.quantity,
            `${next.productName || "Accessory"} quantity`
          )
        : 0;
      if (originalQuantity === nextQuantity) {
        if (next) next.lotAllocations = originalBreakdown.lotAllocations;
        return;
      }
      const inventory = inventories.get(accessoryId);
      if (!inventory) {
        throw new Error(`${original.productName || "Accessory"} inventory was not found.`);
      }
      const before = inventoryTotal(inventory);
      if (nextQuantity < originalQuantity) {
        const quantityReturned = originalQuantity - nextQuantity;
        const result = restoreOrderItemQuantity(
          inventory,
          original,
          quantityReturned,
          legacyReturnRefs.get(accessoryId)
        );
        if (next) next.lotAllocations = result.remainingAllocations;
        logs.push(stockLogData({
          accessoryId,
          accessory: inventory.parent,
          quantity: quantityReturned,
          before,
          type: "return",
          notes: result.legacyQuantity > 0
            ? inventory.tracked
              ? `Order ${currentOrder.receiptNo} edited - stock restored to legacy order return lot`
              : `Order ${currentOrder.receiptNo} edited - old stock restored`
            : `Order ${currentOrder.receiptNo} edited - stock restored`,
          actor,
          allocations: result.restoredAllocations,
        }));
      } else {
        if (!inventory.tracked && originalBreakdown.allocatedQuantity > 0) {
          throw new Error(
            `${original.productName || "Accessory"} has saved allocations but its inventory is not lot-tracked.`
          );
        }
        const quantityAdded = nextQuantity - originalQuantity;
        const result = consumeOrderInventory(inventory, quantityAdded);
        next.lotAllocations = [
          ...originalBreakdown.lotAllocations,
          ...result.allocations,
        ];
        logs.push(stockLogData({
          accessoryId,
          accessory: inventory.parent,
          quantity: -quantityAdded,
          before: result.before,
          type: "sell",
          notes: inventory.tracked
            ? `Order ${currentOrder.receiptNo} edited - quantity increased`
            : `Order ${currentOrder.receiptNo} edited - quantity increased from old stock`,
          actor,
          allocations: result.allocations,
        }));
      }
    });

    nextById.forEach((next, accessoryId) => {
      if (originalById.has(accessoryId)) return;
      const inventory = inventories.get(accessoryId);
      if (!inventory) {
        throw new Error(`${next.productName || "Accessory"} inventory was not found.`);
      }
      const quantity = positiveInteger(
        next.quantity,
        `${next.productName || "Accessory"} quantity`
      );
      const result = consumeOrderInventory(inventory, quantity);
      next.lotAllocations = result.allocations;
      logs.push(stockLogData({
        accessoryId,
        accessory: inventory.parent,
        quantity: -quantity,
        before: result.before,
        type: "sell",
        notes: inventory.tracked
          ? `Order ${currentOrder.receiptNo} edited - item added`
          : `Order ${currentOrder.receiptNo} edited - item added from old stock`,
        actor,
        allocations: result.allocations,
      }));
    });

    inventories.forEach((inventory) =>
      writeInventoryState(transaction, inventory, actor)
    );
    transaction.update(orderRef, { ...nextOrderData, ...completionFields, items: nextItems });
    logs.forEach((log) => transaction.set(doc(collection(database, "stockLogs")), log));
    return nextItems;
  });
};

// Snapshot maps can have different field order despite containing the same data.
// Sort object keys recursively, preserving item/allocation array order and values.
const cancellationItemsSignature = (items) =>
  JSON.stringify(items || [], (_key, value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return value;
    return Object.keys(value).sort().reduce((sorted, key) => {
      sorted[key] = value[key];
      return sorted;
    }, Object.create(null));
  });

export const cancelSaleOrderWithLots = async (database, orderPreview, actor) => {
  const orderRef = doc(database, "saleOrders", orderPreview.id);
  const savedSnap = await getDoc(orderRef);
  if (!savedSnap.exists()) throw new Error("Order not found.");
  const savedOrder = savedSnap.data();
  const savedItems = cancellationItemsSignature(savedOrder.items);
  const resolvedItems = await resolveSaleOrderCancellationItems(database, savedOrder.items);
  // The Excel import records accessory descriptions but never consumes stock.
  // Restoring those rows would incorrectly increase inventory on cancellation.
  const restoreAccessoryStock = !isExcelImportedSaleOrder(savedOrder);
  const accessoryItems = restoreAccessoryStock ? resolvedItems.accessoryItems : [];
  const accessoryIds = restoreAccessoryStock
    ? validateAccessoryIds(Array.from(new Set(accessoryItems.map((item) => item.productId))))
    : [];
  const refsByAccessory = await loadInventoryRefs(database, accessoryIds);
  const legacyReturnRefs = prepareLegacyReturnRefs(database, refsByAccessory);
  const productRefs = resolvedItems.productIds.map(
    (productId) => doc(database, "products", productId)
  );

  return runTransaction(database, async (transaction) => {
    const currentSnap = await transaction.get(orderRef);
    if (!currentSnap.exists()) throw new Error("Order not found.");
    const currentOrder = currentSnap.data();
    const currentRestoreAccessoryStock = !isExcelImportedSaleOrder(currentOrder);
    if (
      restoreAccessoryStock !== currentRestoreAccessoryStock ||
      savedItems !== cancellationItemsSignature(currentOrder.items)
    ) {
      throw new Error("Order data changed. Reload and try again.");
    }
    for (const check of resolvedItems.identityChecks) {
      const snapshot = await transaction.get(check.ref);
      if (snapshot.exists() !== check.exists) {
        throw new Error("Order inventory changed. Reload and try again.");
      }
    }
    const inventories = await readInventories(
      transaction,
      database,
      refsByAccessory,
      { allowLegacy: true }
    );
    for (const productRef of productRefs) {
      const productSnap = await transaction.get(productRef);
      if (!productSnap.exists()) {
        throw new Error(`Product ${productRef.id} was not found.`);
      }
    }
    const logs = [];

    accessoryItems.forEach((item) => {
      const inventory = inventories.get(item.productId);
      if (!inventory) throw new Error("Order inventory changed. Reload and try again.");
      const breakdown = getOrderItemInventoryBreakdown(item);
      const before = inventoryTotal(inventory);
      let notes = `Order ${currentOrder.receiptNo} cancelled`;
      const result = restoreOrderItemQuantity(
        inventory,
        item,
        breakdown.quantity,
        legacyReturnRefs.get(item.productId)
      );
      if (result.legacyQuantity > 0) {
        notes += inventory.tracked
          ? " - restored to legacy order return lot"
          : " - restored to old stock";
      }

      logs.push(stockLogData({
        accessoryId: item.productId,
        accessory: inventory.parent,
        quantity: breakdown.quantity,
        before,
        type: "return",
        notes,
        actor,
        allocations: result.restoredAllocations,
      }));
    });

    inventories.forEach((inventory) =>
      writeInventoryState(transaction, inventory, actor)
    );
    productRefs.forEach((productRef) => transaction.update(productRef, {
      status: "ว่าง", sales: "", soldDate: null, soldTo: null, updatedAt: new Date(), updatedBy: actorName(actor),
    }));
    logs.forEach((log) => transaction.set(doc(collection(database, "stockLogs")), log));
    transaction.delete(orderRef);
  });
};

export const formatLotAllocations = (allocations) =>
  (allocations || [])
    .filter((allocation) => Number(allocation.quantity) > 0)
    .map((allocation) => `${allocation.lotNo || allocation.lotId}: ${allocation.quantity}`)
    .join(", ");

export const formatOrderItemAllocations = (item) => {
  const formattedLots = formatLotAllocations(item && item.lotAllocations);
  try {
    const { legacyQuantity } = getOrderItemInventoryBreakdown(item || {});
    return [
      legacyQuantity > 0 ? `Old stock: ${legacyQuantity}` : "",
      formattedLots,
    ].filter(Boolean).join(", ");
  } catch (error) {
    return formattedLots;
  }
};
